import { z } from 'zod';
import { codigoDeCatalogoSchema } from '../tipos.js';
/**
 * Catálogo de medicamentos · la lista de la que el paciente elige su droga.
 *
 * Definición de Fundación (Ezequiel Monteverde, 26/09/2026): **la medicación se
 * carga estructurada.** El paciente elige el principio activo de una lista que
 * arma Fundación (unos veinte, cada uno con una o dos presentaciones) y escribe
 * sólo dos números: la potencia en miligramos y el intervalo en horas. Nada de
 * texto libre.
 *
 * **Es un catálogo de red, sin institución.** La lista es de Fundación y es la
 * misma para todas las instituciones que usan PASOS+, igual que el catálogo de
 * eventos. No tiene datos de ningún paciente: tiene nombres de drogas. Por eso
 * la tabla no lleva `tenant_id` ni RLS, y lo que la protege es que la aplicación
 * no la puede escribir: sólo el comando de carga, con su marca propia
 * (`app.carga_de_catalogo`), y sólo para agregar (D-256).
 *
 * **Versionado y de sólo agregar.** Una entrada no se edita ni se borra: si
 * Fundación cambia su presentación o la retira, se agrega una fila nueva con la
 * versión siguiente. El estado vigente de una entrada es su fila de versión más
 * alta. Así un `MedicationStatement` de hace seis meses se sigue leyendo contra
 * la entrada que era cuando se respondió.
 */
export const ESTADOS_DE_ENTRADA = ['vigente', 'retirado'];
export const estadoDeEntradaSchema = z.enum(ESTADOS_DE_ENTRADA);
/** Una línea de texto corta del catálogo, sin saltos de línea. */
const textoDeCatalogo = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[^\n\r\t]+$/, 'sin saltos de línea ni tabulaciones');
/**
 * Una entrada del catálogo. La presentación va partida en forma y concentración
 * porque así la da alfabeta, que es de donde Fundación arma la lista.
 */
export const entradaDeMedicamentoSchema = z.strictObject({
    /** Estable para siempre: es lo que guarda la respuesta y el `MedicationStatement`. */
    codigo: codigoDeCatalogoSchema,
    principioActivo: textoDeCatalogo,
    /** Comprimido, cápsula, jeringa prellenada. */
    forma: textoDeCatalogo,
    /** "500 mg", "40 mg/0,4 ml". Texto: la concentración no siempre es un número. */
    concentracion: textoDeCatalogo,
    estado: estadoDeEntradaSchema,
});
/** Lo que devuelve la API: la versión y el estado vigente de cada entrada. */
export const catalogoDeMedicamentosSchema = z.strictObject({
    /** Versión del catálogo: la más alta cargada. Es también el `ETag`. */
    version: z.number().int().nonnegative(),
    /**
     * Todas las entradas, vigentes y retiradas. La PWA ofrece sólo las vigentes;
     * el equipo necesita las retiradas para leer las respuestas viejas.
     */
    entradas: z.array(entradaDeMedicamentoSchema),
});
/** Cómo se nombra una entrada en pantalla y en el `display` de FHIR. */
export function etiquetaDeMedicamento(entrada) {
    return `${entrada.principioActivo} · ${entrada.forma} ${entrada.concentracion}`;
}
// ─────────────────────────────────────────────────────────────────────────────
// El archivo
// ─────────────────────────────────────────────────────────────────────────────
/** Las columnas, en este orden. Un archivo con otro encabezado no se carga. */
export const COLUMNAS_DEL_CSV = [
    'codigo',
    'principio_activo',
    'forma',
    'concentracion',
    'estado',
];
/**
 * Parte una línea de CSV, con comillas dobles para los valores que llevan coma
 * ("paracetamol, cafeína"). Dos comillas seguidas dentro de un valor son una.
 */
export function partirLinea(linea) {
    const valores = [];
    let actual = '';
    let entreComillas = false;
    for (let indice = 0; indice < linea.length; indice += 1) {
        const caracter = linea[indice];
        if (entreComillas && caracter === '"' && linea[indice + 1] === '"') {
            actual += '"';
            indice += 1;
        }
        else if (caracter === '"') {
            entreComillas = !entreComillas;
        }
        else if (caracter === ',' && !entreComillas) {
            valores.push(actual);
            actual = '';
        }
        else {
            actual += caracter;
        }
    }
    valores.push(actual);
    return valores;
}
/**
 * Lee el archivo del catálogo. Las líneas que empiezan con `#` son comentarios
 * (el encabezado que dice si el archivo es provisorio) y las vacías se saltean.
 *
 * No carga a medias: devuelve todos los problemas juntos, y quien llama no carga
 * nada si hay alguno. Una lista de Fundación con una fila rota se corrige y se
 * vuelve a correr; cargar las otras diecinueve dejaría un catálogo que nadie
 * aprobó.
 */
export function leerCsvDeMedicamentos(texto) {
    const entradas = [];
    const problemas = [];
    const vistos = new Set();
    let encabezadoLeido = false;
    const lineas = texto.split(/\r?\n/);
    lineas.forEach((cruda, indice) => {
        const numero = indice + 1;
        const linea = cruda.trim();
        if (linea === '' || linea.startsWith('#'))
            return;
        const valores = partirLinea(linea).map((valor) => valor.trim());
        if (!encabezadoLeido) {
            encabezadoLeido = true;
            if (valores.join(',') !== COLUMNAS_DEL_CSV.join(',')) {
                problemas.push(`línea ${numero}: el encabezado tiene que ser ${COLUMNAS_DEL_CSV.join(',')}`);
            }
            return;
        }
        if (valores.length !== COLUMNAS_DEL_CSV.length) {
            problemas.push(`línea ${numero}: tiene ${valores.length} columnas y son ${COLUMNAS_DEL_CSV.length}`);
            return;
        }
        const [codigo, principioActivo, forma, concentracion, estado] = valores;
        const leida = entradaDeMedicamentoSchema.safeParse({
            codigo,
            principioActivo,
            forma,
            concentracion,
            estado,
        });
        if (!leida.success) {
            const campos = leida.error.issues.map((issue) => issue.path.join('.')).join(', ');
            problemas.push(`línea ${numero}: valor inválido en ${campos}`);
            return;
        }
        if (vistos.has(leida.data.codigo)) {
            problemas.push(`línea ${numero}: el código ${leida.data.codigo} está repetido`);
            return;
        }
        vistos.add(leida.data.codigo);
        entradas.push(leida.data);
    });
    if (!encabezadoLeido)
        problemas.push('el archivo no tiene encabezado');
    return { entradas, problemas };
}
// ─────────────────────────────────────────────────────────────────────────────
// El módulo generado (P-102)
// ─────────────────────────────────────────────────────────────────────────────
/** Dónde vive el módulo, relativo a este archivo, y el comando que lo escribe. */
export const MODULO_GENERADO = 'medicamentos.generado.ts';
export const COMANDO_QUE_GENERA = 'pnpm --filter @pasos/api catalogo:generar';
/**
 * Un texto como literal de TypeScript, con las comillas que usaría el formateador
 * del repositorio: simples, salvo que el texto lleve una simple y ninguna doble.
 * Así el módulo generado pasa el lint tal como sale.
 */
function literal(texto) {
    const escapado = texto.replaceAll('\\', '\\\\');
    if (texto.includes("'") && !texto.includes('"'))
        return `"${escapado}"`;
    return `'${escapado.replaceAll("'", "\\'")}'`;
}
/**
 * El texto del módulo TypeScript que lleva el catálogo adentro del código.
 *
 * **Por qué existe (P-102).** El CSV es lo que edita Fundación, pero la imagen de
 * la API lleva sólo `dist/`: en un ambiente de Google el archivo no está, y
 * cargar el catálogo pedía una corrida aparte desde otra imagen. Con el módulo,
 * las entradas viajan compiladas como cualquier otro dato del paquete y
 * `provisionar` las carga al final de su corrida, sin un paso a mano.
 *
 * El CSV sigue siendo la fuente: el módulo se genera de él con
 * `COMANDO_QUE_GENERA`, y un test falla si los dos no dicen lo mismo. Es el
 * mismo trato que las definiciones de FHIR: un archivo generado al lado de su
 * fuente, y un test que no los deja separarse. Pura: la usan el comando y el
 * test, y la salida no depende de nada más que de las entradas.
 */
export function moduloGeneradoDelCatalogo(entradas) {
    const filas = entradas.map((entrada) => `  {\n` +
        `    codigo: ${literal(entrada.codigo)},\n` +
        `    principioActivo: ${literal(entrada.principioActivo)},\n` +
        `    forma: ${literal(entrada.forma)},\n` +
        `    concentracion: ${literal(entrada.concentracion)},\n` +
        `    estado: ${literal(entrada.estado)},\n` +
        `  },\n`);
    return (`// Generado desde medicamentos.csv con \`${COMANDO_QUE_GENERA}\`.\n` +
        '// No se edita a mano: se edita el CSV y se vuelve a generar (P-102).\n' +
        "import type { EntradaDeMedicamento } from './medicamentos.js';\n\n" +
        'export const MEDICAMENTOS_DEL_REPOSITORIO: readonly EntradaDeMedicamento[] = [\n' +
        filas.join('') +
        '];\n');
}
function mismaEntrada(a, b) {
    return (a.principioActivo === b.principioActivo &&
        a.forma === b.forma &&
        a.concentracion === b.concentracion &&
        a.estado === b.estado);
}
/**
 * Compara el archivo contra lo cargado y dice qué agregar. Pura: la usa el
 * comando de carga y la usan los tests, sin base.
 *
 * `cargadas` es el estado vigente de cada entrada (su fila de versión más alta).
 * Si el plan viene vacío, el comando no escribe nada y la versión no se mueve:
 * correrlo dos veces con el mismo archivo es inocuo.
 */
export function planDeCarga(cargadas, delArchivo) {
    const porCodigo = new Map(cargadas.map((entrada) => [entrada.codigo, entrada]));
    const enElArchivo = new Set(delArchivo.map((entrada) => entrada.codigo));
    const nuevas = delArchivo.filter((entrada) => !porCodigo.has(entrada.codigo));
    const cambiadas = delArchivo.filter((entrada) => {
        const cargada = porCodigo.get(entrada.codigo);
        return cargada !== undefined && !mismaEntrada(cargada, entrada);
    });
    const retiradas = cargadas
        .filter((entrada) => entrada.estado === 'vigente' && !enElArchivo.has(entrada.codigo))
        .map((entrada) => ({ ...entrada, estado: 'retirado' }));
    return { nuevas, cambiadas, retiradas, filas: [...nuevas, ...cambiadas, ...retiradas] };
}
/**
 * El estado vigente de cada entrada, a partir de todas sus filas: la de versión
 * más alta. Es la misma regla que aplica la consulta de la API.
 */
export function estadoVigente(filas) {
    const ultima = new Map();
    for (const fila of filas) {
        const previa = ultima.get(fila.codigo);
        if (!previa || fila.version > previa.version)
            ultima.set(fila.codigo, fila);
    }
    return [...ultima.values()]
        .sort((a, b) => a.codigo.localeCompare(b.codigo))
        .map(({ version: _version, ...entrada }) => entrada);
}
// ─────────────────────────────────────────────────────────────────────────────
// La búsqueda
// ─────────────────────────────────────────────────────────────────────────────
/** Minúsculas y sin tildes: "Ácido" y "acido" son la misma búsqueda. */
function normalizar(texto) {
    return texto
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .trim();
}
/**
 * Las entradas vigentes que coinciden con lo que se escribió en el buscador.
 *
 * **La búsqueda filtra, no escribe.** Lo tipeado no es una respuesta y no viaja
 * a ningún lado: sirve para achicar la lista, y la respuesta es siempre el
 * código de una entrada. Cada palabra tiene que aparecer en el principio activo
 * o en la presentación, en cualquier orden ("para 500" encuentra paracetamol
 * 500 mg). Sin texto, devuelve todas las vigentes.
 */
export function buscarMedicamentos(entradas, texto) {
    const palabras = normalizar(texto).split(/\s+/).filter(Boolean);
    return entradas
        .filter((entrada) => entrada.estado === 'vigente')
        .filter((entrada) => {
        const donde = normalizar(etiquetaDeMedicamento(entrada));
        return palabras.every((palabra) => donde.includes(palabra));
    })
        .sort((a, b) => etiquetaDeMedicamento(a).localeCompare(etiquetaDeMedicamento(b), 'es'));
}
//# sourceMappingURL=medicamentos.js.map